/**
 * Entity KB property mutations via dash-api REST
 * with direct REST calls to dash-api.
 *
 * Response shape preserved:
 *   create/update → { item: {...}, newRevision: number }
 *   delete        → { propertyItemId: string, newRevision: number }
 */

import {getAwpClientHeaders} from '../../session/awpRequestHeaders';

const AWP_BASE = (process.env.REACT_APP_AWP_API_BASE || process.env.REACT_APP_AWP_API_URL || '')
    .replace(/\/$/, '');

export async function fetchEntityPropertiesRevision(entityId) {
    const res = await fetch(`${AWP_BASE}/api/entities/${encodeURIComponent(entityId)}/properties`, {
        headers: getAwpClientHeaders(),
    });
    if (!res.ok) {
        return null;
    }
    const data = await res.json().catch(() => null);
    return Number.isInteger(data?.revision) && data.revision >= 0 ? data.revision : null;
}

async function apiCall(method, path, body) {
    const res = await fetch(`${AWP_BASE}${path}`, {
        method,
        headers: {
            'Content-Type': 'application/json',
            ...getAwpClientHeaders(),
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const code = res.status === 409 ? 'aborted' : 'unknown';
        const error = new Error(err.message || err.error || `HTTP ${res.status}`);
        error.code = code;
        throw error;
    }

    return res.json();
}

function assertMutationResponseShape(data, operationName) {
    if (!data || typeof data !== 'object') {
        throw new Error(`[entityPropertyMutations] ${operationName} returned an invalid response.`);
    }
    if (!Number.isInteger(data.newRevision) || data.newRevision < 0) {
        throw new Error(`[entityPropertyMutations] ${operationName} did not return a valid newRevision.`);
    }
}

function normalizeMutationResponse(data, operationName) {
    assertMutationResponseShape(data, operationName);

    if (data.item && typeof data.item === 'object') {
        return {item: data.item, newRevision: data.newRevision};
    }

    if (typeof data.propertyItemId === 'string' && data.propertyItemId.length > 0) {
        return {
            item: {propertyItemId: data.propertyItemId},
            newRevision: data.newRevision,
        };
    }

    throw new Error(`[entityPropertyMutations] ${operationName} did not return an item payload.`);
}

export async function createEntityProperty({entityId, expectedRevision, property}) {
    const result = await apiCall('POST',
        `/api/entities/${encodeURIComponent(entityId)}/properties`,
        {expectedRevision, property});
    return normalizeMutationResponse(result, 'createEntityProperty');
}

export async function updateEntityProperty({entityId, propertyItemId, expectedRevision, patch}) {
    const result = await apiCall('PATCH',
        `/api/entities/${encodeURIComponent(entityId)}/properties/${encodeURIComponent(propertyItemId)}`,
        {propertyItemId, expectedRevision, patch});
    return normalizeMutationResponse(result, 'updateEntityProperty');
}

export async function deleteEntityProperty({entityId, propertyItemId, expectedRevision}) {
    const result = await apiCall('DELETE',
        `/api/entities/${encodeURIComponent(entityId)}/properties/${encodeURIComponent(propertyItemId)}`,
        {propertyItemId, expectedRevision});
    return normalizeMutationResponse(result, 'deleteEntityProperty');
}
